import { handleFreeReturn } from '@lambda/commons/helpers';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  memo,
} from 'react';
import Image from 'next/image';
import { CartLineT, LineItemType } from '@lambda/apis/src/cart/types';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import { RC_DURATION } from '@lambda/reebelo';
import { AppCtx } from 'pages/_app';
import { useRouter } from 'next/router';
import { ZapIcon } from 'lucide-react';
import cn from 'classnames';
import { CtaButton } from '@/components/Cta/Cta';
import { priceFormatSuperscript } from '@/lib/price';
import settings, { priceFormater } from '@/settings';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import ReebeloCarePanel from './ReebeloCarePanel';
import ReebeloCareModal from './ReebeloCareModal';
import { CartWarrantyT } from '@/lib/reebelocare/types';
import { buildReebeloCareLine, generateCartWarrantyLabel } from '../helpers';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import { splitProductTitle } from '../../product/sections/main/right/product/helpers';
import CheckoutModal from '../CheckoutModal';
import StickyCheckoutBar from '../StickyCheckoutBar';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import { hasSegmentConsent } from '@/lib/utils/cookie-consent';
import { isBot } from '@/lib/utils/bot';
import { TimeoutError, withTimeout } from '@/lib/utils/withTimeout';
import { trackCartError, trackCartEvent } from '@/lib/datadog-rum';
import { trackCheckoutStarted } from '@/lib/analytics/trackCheckout';
import {
  trackReebeloCareAdded,
  trackReebeloCareRemoved,
} from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import { ToBeRemovedT } from '@/utils/hooks/cart/types';
import useOrderDiscounts from '@/lib/order-discounts/useOrderDiscounts';
import { getCartDiscountSubtotal } from '@/lib/order-discounts/helpers';
import { getCartSummary } from '@/lib/cart-summary';
import OrderDiscountBadge, {
  DISCOUNT_GREEN,
} from '@/components/order-discounts/OrderDiscountBadge';
import CartTradeIn from '@/components/tradein/CartTradeIn';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

const CART_UPDATE_TIMEOUT_MS = 20_000;
const SEGMENT_USER_TIMEOUT_MS = 2_000;
const CHECKOUT_REQUEST_TIMEOUT_MS = 20_000;
// How long the checkout buttons can stay disabled before it is reported
const STUCK_BUTTON_REPORT_MS = 10_000;

type CheckoutEntryPoint = 'desktop' | 'mobile_modal' | 'mobile_direct';

type CheckoutOutcome =
  | 'redirected'
  | 'requires_review'
  | 'no_checkout_url'
  | 'timeout'
  | 'error';

// Runs `work` and records how long it took as `<step>Ms` in `details`
async function measure<T>(
  details: Record<string, unknown>,
  step: string,
  work: () => Promise<T>,
): Promise<T> {
  const startedAt = performance.now();

  try {
    return await work();
  } finally {
    // eslint-disable-next-line no-param-reassign
    details[`${step}Ms`] = Math.round(performance.now() - startedAt);
  }
}

// Segment's anonymous id, which the checkout API uses to link the session
const getSegmentAnonymousId = async (): Promise<string | undefined> => {
  if (!hasSegmentConsent() || isBot()) {
    return undefined;
  }

  const { getAnalytics } = await import(
    '@/lib/analytics/vendor/segment/segment'
  );
  const user = await getAnalytics()?.then(([a]) => a.user());

  return user ? String(user.anonymousId()) : undefined;
};

const requestCheckout = async (query: Record<string, string>) => {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    CHECKOUT_REQUEST_TIMEOUT_MS,
  );

  try {
    const res = await fetch(`/api/checkout?${new URLSearchParams(query)}`, {
      signal: controller.signal,
    });

    return { status: res.status, body: await res.json() };
  } catch (error) {
    if (controller.signal.aborted) {
      throw new TimeoutError('checkout request', CHECKOUT_REQUEST_TIMEOUT_MS);
    }

    throw error;
  } finally {
    clearTimeout(timer);
  }
};

const buildReebeloCareTrackData = (
  device: CartLineT,
  warranty: WarrantyT,
  entryPoint: ReebeloCareTrackRequest['entry_point'] = 'Cart',
): ReebeloCareTrackRequest => ({
  entry_point: entryPoint,
  reebelocare_id: warranty.id,
  device_psku: device.psku ?? '',
  device_vendor: device.vendor,
  device_sku: device.sku,
  duration: warranty.duration.toString() as RC_DURATION,
});

const PaymentMethods = memo(() => (
  <div className="mt-4 flex items-center justify-center gap-2">
    {Object.keys(settings.payment_methods).map((p) => (
      <div key={p} className="relative h-5">
        <Image
          src={getEdgeUrl(`icons/payments/${p}.svg`)}
          title={settings.payment_methods[p]}
          alt={p}
          height={15}
          width={12}
          className="h-full w-auto object-fill"
          style={{
            filter:
              'drop-shadow(0 0 1px rgba(0, 0, 0, 0.05)) drop-shadow(0 1px 1px rgba(0, 0, 0, 0.04)) drop-shadow(1px 2px 1px rgba(0, 0, 0, 0.03)) drop-shadow(2px 4px 2px rgba(0, 0, 0, 0.01)) drop-shadow(2px 6px 2px rgba(0, 0, 0, 0.00))',
          }}
        />
      </div>
    ))}
  </div>
));

PaymentMethods.displayName = 'PaymentMethods';

const Summary = memo(() => {
  const { cartLines, getExpressShippingForProduct } = useCartContext();

  const streamlinedCartLines = useMemo(
    () => cartLines.filter((line) => line.type === LineItemType.PRODUCT),
    [cartLines],
  );

  return (
    <div className="lg:hidden">
      <div className="space-y-6">
        {streamlinedCartLines.map((line) => {
          const { primaryTitle, secondaryTitle } = splitProductTitle(
            line.title,
          );

          const expressShipping = getExpressShippingForProduct(line.id);

          return (
            <div key={line.id}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded border">
                    <div className="h-6 w-6">
                      <Image
                        src={line?.image ?? ''}
                        alt={line.title}
                        width={50}
                        height={50}
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-gray-700 text-xxs font-medium text-white">
                      {line.quantity}
                    </div>
                  </div>
                  <div className="text-xs text-gray-700 xxs:text-sm">
                    <div className="font-medium leading-tight">
                      {primaryTitle}{' '}
                    </div>
                    {secondaryTitle && (
                      <div className="text-xs text-gray-700/75">
                        {secondaryTitle}
                      </div>
                    )}
                  </div>
                </div>
                <div translate="no">
                  {priceFormatSuperscript(
                    priceFormater.long(
                      (Number(line.price) * line.quantity) / 100 || 0,
                    ),
                  )}
                </div>
              </div>
              {expressShipping && (
                <div className="ml-14 flex items-center justify-between text-xs text-gray-700 xxs:text-sm">
                  <div className="flex items-center justify-center gap-1 text-xs font-medium leading-tight">
                    <ZapIcon className="h-4 w-4 shrink-0" />
                    {expressShipping.title}
                  </div>
                  <div translate="no">
                    {priceFormatSuperscript(
                      priceFormater.long(
                        (Number(expressShipping.price) *
                          expressShipping.quantity) /
                          100 || 0,
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="my-4 h-px w-full bg-[#F2F2F2]" />
    </div>
  );
});

Summary.displayName = 'Summary';

const RightSection = memo(
  ({
    warranties,
    reebeloCareChecked,
    setReebeloCareChecked,
    totalReebeloCarePrice,
    reebeloCareSubtotal,
  }: {
    warranties: CartWarrantyT;
    reebeloCareChecked: boolean;
    setReebeloCareChecked: Dispatch<SetStateAction<boolean>>;
    totalReebeloCarePrice: number;
    reebeloCareSubtotal: number;
  }) => {
    const {
      cartLines,
      cartLoading,
      cart,
      addLinesToCart,
      removeLinesFromCart,
      setReviewLines,
      initializeCart,
    } = useCartContext();
    const { zipcodeStateData } = useContext(AppCtx);
    const { customer } = useCustomerContext();
    const router = useRouter();
    // In the purchase flow ReebeloCare is chosen per device on the line items
    // and is in the cart as soon as it's chosen: no protection panel here, no
    // upsell at checkout, and the plans are just cart lines in the totals.
    const isPurchaseFlow = isPurchaseFlowEnabled(customer?.email);

    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [showMobileCheckoutModal, setShowMobileCheckoutModal] =
      useState(false);
    const [showWarrantyDetails, setShowWarrantyDetails] = useState(false);
    const [usps, setUsps] = useState<string>('');

    // The checkout buttons are disabled while the cart or checkout is busy.
    // Report it when that lasts long enough to look stuck, since a disabled
    // button can't be clicked and so never shows up as a click event.
    useEffect(() => {
      if (!cartLoading && !isCheckingOut) {
        return undefined;
      }

      const timer = setTimeout(
        () =>
          trackCartError('checkout_button_stuck_loading', {
            cartLoading,
            isCheckingOut,
          }),
        STUCK_BUTTON_REPORT_MS,
      );

      return () => clearTimeout(timer);
    }, [cartLoading, isCheckingOut]);

    const categoryPks = useMemo(
      () =>
        Array.from(
          new Set(
            cartLines
              .map((line) => line.category?.pk)
              .filter((pk): pk is string => Boolean(pk)),
          ),
        ),
      [cartLines],
    );

    const conditions = useMemo(
      () =>
        Array.from(
          new Set(
            cartLines
              .map(
                (line) =>
                  line.variants?.find((variant) => variant.name === 'Condition')
                    ?.value,
              )
              .filter((value): value is string => Boolean(value)),
          ),
        ),
      [cartLines],
    );

    useEffect(() => {
      const generateUspsAsync = async () => {
        const _usps = await generateCartWarrantyLabel({
          conditions,
          categoryPks,
          freeReturnDays,
        });

        setUsps(_usps);
      };

      generateUspsAsync();
    }, [conditions, categoryPks]);

    const existingWarrantyTotal = useMemo(() => {
      const warrantyDeviceIds = new Set(Object.keys(warranties));

      return cartLines
        .filter(
          (line) =>
            line.type === LineItemType.WARRANTY &&
            line.reference?.typeId &&
            warrantyDeviceIds.has(line.reference.typeId),
        )
        .reduce((acc, line) => acc + (line.price * line.quantity) / 100, 0);
    }, [cartLines, warranties]);

    const planLinesTotal = useMemo(
      () =>
        cartLines
          .filter((line) => line.type === LineItemType.WARRANTY)
          .reduce((acc, line) => acc + (line.price * line.quantity) / 100, 0),
      [cartLines],
    );

    const totalCart = useMemo(
      () =>
        isPurchaseFlow
          ? Number(cart?.amount.subTotal) / 100 || 0
          : (Number(cart?.amount.subTotal) / 100 || 0) -
            existingWarrantyTotal +
            (reebeloCareChecked ? totalReebeloCarePrice : 0),
      [
        cart,
        isPurchaseFlow,
        reebeloCareChecked,
        totalReebeloCarePrice,
        existingWarrantyTotal,
      ],
    );

    const { itemCount, itemsTotal, shippingTotal } = useMemo(
      () => getCartSummary(cartLines),
      [cartLines],
    );

    let reebeloCareTotal = reebeloCareChecked ? totalReebeloCarePrice : 0;

    if (isPurchaseFlow) {
      reebeloCareTotal = planLinesTotal;
    }

    const showsProtectionPanel =
      !isPurchaseFlow && Object.keys(warranties).length > 0;

    // Selected ReebeloCare is charged in the total above, so it earns a
    // threshold too — even though it has no cart line until checkout.
    const discountSubtotal = useMemo(
      () => getCartDiscountSubtotal(cartLines, reebeloCareSubtotal),
      [cartLines, reebeloCareSubtotal],
    );
    const orderDiscount = useOrderDiscounts(discountSubtotal, customer?.email);
    const orderDiscountAmount = orderDiscount.discount / 100;

    // Fires ReebeloCare Added/Removed when the user toggles the plan on/off (one event per covered device).
    const handleReebeloCareChange = useCallback<
      Dispatch<SetStateAction<boolean>>
    >(
      (value) => {
        const next =
          typeof value === 'function' ? value(reebeloCareChecked) : value;

        setReebeloCareChecked(next);

        if (next === reebeloCareChecked) {
          return;
        }

        Object.entries(warranties).forEach(([lineId, { productWarranty }]) => {
          const device = cartLines.find((l) => l.id === lineId);

          if (!device) {
            return;
          }

          const data = buildReebeloCareTrackData(device, productWarranty);

          if (next) {
            trackReebeloCareAdded(data);
          } else {
            trackReebeloCareRemoved(data);
          }
        });
      },
      [reebeloCareChecked, warranties, cartLines],
    );

    // `null` leaves the cart's ReebeloCare lines as they are (the purchase
    // flow, where they're added on the line items); otherwise the cart-wide
    // choice adds or removes them on the way to checkout.
    const handleCheckout = useCallback(
      async (
        _reebeloCareChecked: boolean | null,
        entryPoint: CheckoutEntryPoint,
      ) => {
        const startedAt = performance.now();
        const details: Record<string, unknown> = {
          entryPoint,
          reebeloCareChecked: _reebeloCareChecked,
          hasCart: Boolean(cart),
        };

        trackCartEvent('checkout_click', details);

        if (!cart) {
          trackCartError('checkout_no_cart', details);

          return;
        }

        setIsCheckingOut(true);

        let outcome: CheckoutOutcome = 'error';
        let failure: unknown;

        const updateReebeloCare = async () => {
          if (_reebeloCareChecked === null) {
            // Nothing to add or remove.
          } else if (_reebeloCareChecked) {
            // Every device without its plan yet, in one request.
            await addLinesToCart(
              Object.entries(warranties).flatMap(
                ([lineId, { productWarranty }]) => {
                  const lineItem = cartLines.find((l) => l.id === lineId);
                  const alreadyInCart = cartLines.some(
                    (l) =>
                      l.type === LineItemType.WARRANTY &&
                      l.reference?.typeId === lineId,
                  );

                  return lineItem && !alreadyInCart
                    ? [buildReebeloCareLine(lineItem, productWarranty)]
                    : [];
                },
              ),
            );
          } else {
            const warrantyDeviceIds = new Set(Object.keys(warranties));
            const warrantyCartLines = cartLines.filter(
              (line) =>
                line.type === LineItemType.WARRANTY &&
                line.reference?.typeId &&
                warrantyDeviceIds.has(line.reference.typeId),
            );

            await removeLinesFromCart(warrantyCartLines, {
              skipReebeloCareTracking: true,
            });
          }
        };

        try {
          await measure(details, 'cartUpdate', () =>
            withTimeout(
              updateReebeloCare(),
              CART_UPDATE_TIMEOUT_MS,
              'cart update',
            ),
          );

          const { userAgent } = navigator;
          const query: Record<string, string> = { cartId: cart.id, userAgent };

          // The session id is optional, so don't let a Segment script that
          // never loads keep the buttons disabled.
          const sessionId = await measure(details, 'segment', () =>
            withTimeout(
              getSegmentAnonymousId(),
              SEGMENT_USER_TIMEOUT_MS,
              'segment user',
            ).catch((error) => {
              details.segmentFailed =
                error instanceof TimeoutError ? 'timeout' : 'error';

              return undefined;
            }),
          );

          if (sessionId) {
            query.sessionId = sessionId;
          }

          if (settings.store === 'reebelo-au' && zipcodeStateData) {
            query.zipcode = zipcodeStateData.zipcode;
            query.state = zipcodeStateData.state;
          }

          if (router.locale === 'fr') {
            query.lang = 'fr';
          }

          if (customer) {
            query.customerId = customer.reebeloId;
          }

          const { status, body: response } = await measure(
            details,
            'checkoutRequest',
            () => requestCheckout(query),
          );

          details.status = status;

          if (status < 200 || status >= 300) {
            throw new Error(`Checkout request failed with status ${status}`);
          }

          if (response.requiresReview) {
            setReviewLines((response.reviewLines ?? []) as ToBeRemovedT[]);

            await initializeCart();

            outcome = 'requires_review';

            return;
          }

          const { checkoutUrl } = response;

          trackCheckoutStarted({
            cart,
            properties: { email: customer?.email, new_checkout: true },
          });

          if (checkoutUrl) {
            outcome = 'redirected';
            router.push(checkoutUrl);
          } else {
            outcome = 'no_checkout_url';
          }
        } catch (error) {
          outcome = error instanceof TimeoutError ? 'timeout' : 'error';
          failure = error;
        } finally {
          setIsCheckingOut(false);

          details.totalMs = Math.round(performance.now() - startedAt);

          trackCartEvent('checkout_outcome', { ...details, outcome });

          if (outcome !== 'redirected' && outcome !== 'requires_review') {
            trackCartError(`checkout_${outcome}`, details, failure);
          }
        }
      },
      [
        cart,
        warranties,
        cartLines,
        addLinesToCart,
        removeLinesFromCart,
        customer,
        router,
        zipcodeStateData,
      ],
    );

    // Mobile has no toggle — the ReebeloCare decision is the CTA itself, so fire
    // the Added/Removed event on button click before proceeding to checkout.
    const handleMobileCheckout = useCallback(
      (checked: boolean) => {
        Object.entries(warranties).forEach(([lineId, { productWarranty }]) => {
          const device = cartLines.find((l) => l.id === lineId);

          if (!device) {
            return;
          }

          const data = buildReebeloCareTrackData(
            device,
            productWarranty,
            'Cart Pop-up',
          );

          if (checked) {
            trackReebeloCareAdded(data);
          } else {
            trackReebeloCareRemoved(data);
          }
        });

        handleCheckout(checked, 'mobile_modal');
      },
      [warranties, cartLines, handleCheckout],
    );

    // Mobile checkout starts with the ReebeloCare upsell, unless there is no plan
    // to offer for anything in the cart.
    const startMobileCheckout = useCallback(() => {
      if (isPurchaseFlow) {
        handleCheckout(null, 'mobile_direct');

        return;
      }

      if (Object.keys(warranties).length === 0) {
        handleCheckout(false, 'mobile_direct');

        return;
      }

      trackCartEvent('checkout_modal_opened', {
        warrantyCount: Object.keys(warranties).length,
      });

      setShowMobileCheckoutModal(true);
    }, [isPurchaseFlow, warranties, handleCheckout]);

    const generateTotalLabel = () => {
      if (['reebelo-au', 'reebelo-nz', 'quista'].includes(settings.store)) {
        return t`Total Incl. GST`;
      }

      if (['reebelo-uk'].includes(settings.store)) {
        return t`Total Incl. VAT`;
      }

      return t`Total`;
    };
    const totalLabel = generateTotalLabel();

    return (
      <>
        <section className="sticky top-[75px] mt-8 self-start lg:col-span-5 lg:mt-9 xl:col-span-4">
          <div className="flex flex-col">
            <div className="rounded-2xl border bg-white p-4 text-sm shadow-[0_16px_5px_0_rgba(0,_0,_0,_0.00),_0_10px_4px_0_rgba(0,_0,_0,_0.00),_0_6px_4px_0_rgba(0,_0,_0,_0.01),_0_3px_3px_0_rgba(0,_0,_0,_0.02),_0_1px_1px_0_rgba(0,_0,_0,_0.02)] lg:p-5">
              <div className="flex h-7 items-center justify-between gap-2">
                <h2 className="text-base font-medium">{t`Summary`}</h2>
                <OrderDiscountBadge
                  state={orderDiscount}
                  className="hidden lg:block"
                />
              </div>
              <div className="mt-3">
                <Summary />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between gap-4">
                  <span className="leading-tight">
                    {t`Items (${{ count: String(itemCount) }})`}
                  </span>
                  <span translate="no">
                    {priceFormatSuperscript(
                      priceFormater.long(itemsTotal / 100),
                    )}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="leading-tight">{t`Shipping`}</span>
                  <span translate="no">
                    {shippingTotal > 0 ? (
                      priceFormatSuperscript(
                        priceFormater.long(shippingTotal / 100),
                      )
                    ) : (
                      <span className="inline-flex h-6 items-center justify-center rounded-md bg-[#D6FFE2] px-2 text-xs font-bold uppercase text-[#128452]">
                        {t`Free`}
                      </span>
                    )}
                  </span>
                </div>
                {reebeloCareTotal > 0 && (
                  <div className="flex justify-between gap-4">
                    <span className="leading-tight">{t`ReebeloCare`}</span>
                    <span translate="no">
                      {priceFormatSuperscript(
                        priceFormater.long(reebeloCareTotal),
                      )}
                    </span>
                  </div>
                )}
                {orderDiscount.activeTier && (
                  <div
                    className={cn(
                      'flex justify-between gap-4 font-bold',
                      DISCOUNT_GREEN,
                    )}
                  >
                    <span className="leading-tight">
                      {t`Order discount (-${{
                        percent: orderDiscount.activeTier.percentage,
                      }}%)`}
                    </span>
                    <span translate="no">
                      -
                      {priceFormatSuperscript(
                        priceFormater.long(orderDiscountAmount),
                      )}
                    </span>
                  </div>
                )}
              </div>
              {!isPurchaseFlow && (
                <ReebeloCarePanel
                  className="mt-4 hidden lg:block"
                  warranties={warranties}
                  reebeloCareChecked={reebeloCareChecked}
                  setReebeloCareChecked={handleReebeloCareChange}
                  totalReebeloCarePrice={totalReebeloCarePrice}
                  onShowWarrantyDetails={() => setShowWarrantyDetails(true)}
                />
              )}
              <div
                className={cn(
                  'mt-4 flex justify-between gap-4 border-t pt-4 text-base font-bold',
                  { 'lg:border-t-0 lg:pt-0': showsProtectionPanel },
                  // No separator in the purchase flow: the total sits right
                  // under the lines it adds up.
                  { '!mt-3 border-t-0 !pt-0': isPurchaseFlow },
                )}
              >
                <span className="leading-tight">{totalLabel}</span>
                <span>
                  {priceFormatSuperscript(
                    priceFormater.long(totalCart - orderDiscountAmount),
                  )}
                </span>
              </div>
              <CartTradeIn total={totalCart - orderDiscountAmount} />
              <CtaButton
                variant="dark"
                loading={cartLoading || isCheckingOut}
                onClick={() =>
                  handleCheckout(
                    isPurchaseFlow ? null : reebeloCareChecked,
                    'desktop',
                  )
                }
                id="e2e-cart-checkout-btn"
                className={cn(
                  'hidden w-full !p-3 font-bold lg:block',
                  isPurchaseFlow ? 'mt-3' : 'mt-4',
                )}
              >
                {t`Checkout`}
              </CtaButton>
              <CtaButton
                variant="dark"
                loading={cartLoading || isCheckingOut}
                onClick={startMobileCheckout}
                id="e2e-cart-checkout-btn"
                className={cn(
                  'block w-full !p-3 font-bold lg:hidden',
                  isPurchaseFlow ? 'mt-3' : 'mt-4',
                )}
              >
                {t`Checkout`}
              </CtaButton>
              <PaymentMethods />
              <div className="mt-4 text-center text-xs font-medium">{usps}</div>
            </div>
          </div>
        </section>
        <StickyCheckoutBar
          totalLabel={totalLabel}
          itemCount={itemCount}
          total={totalCart - orderDiscountAmount}
          loading={cartLoading || isCheckingOut}
          onCheckout={startMobileCheckout}
          compact={isPurchaseFlow}
        />
        {!isPurchaseFlow && (
          <CheckoutModal
            showModal={showMobileCheckoutModal}
            setShowModal={setShowMobileCheckoutModal}
            warranties={warranties}
            reebeloCareChecked={reebeloCareChecked}
            setReebeloCareChecked={setReebeloCareChecked}
            totalReebeloCarePrice={totalReebeloCarePrice}
            onCheckoutClick={handleMobileCheckout}
            isCheckingOut={isCheckingOut}
            onShowWarrantyDetails={() => setShowWarrantyDetails(true)}
          />
        )}
        <ReebeloCareModal
          showModal={showWarrantyDetails}
          setShowModal={setShowWarrantyDetails}
        />
      </>
    );
  },
);

RightSection.displayName = 'RightSection';

export default RightSection;
